import { useState, useEffect } from "react";
import { Routes, Route, useNavigate } from "react-router-dom";
import { DashboardLayout } from "../components/DashboardLayout";
import { StatusBadge, PassTypeBadge, formatDate, formatDateTime } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { gateSearch, getReadyForVerification, verifyGatePass, getVerifiedToday, getGatePastDate, getWardenActivePasses, markReturnEntry } from "../lib/api";
import { Search, ShieldCheck, CheckCircle2, Eye, Loader2, Printer, QrCode, Calendar, ArrowDownLeft } from "lucide-react";
import TodayPastPasses from "../components/TodayPastPasses";

const GateOperatorDashboard = () => {
  return (
    <DashboardLayout title="Gate Operator Portal">
      <Routes>
        <Route index element={<GateOperatorHome />} />
      </Routes>
    </DashboardLayout>
  );
};

const GateOperatorHome = () => {
  const [ready, setReady] = useState([]);
  const [verified, setVerified] = useState([]);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [selectedPass, setSelectedPass] = useState(null);
  const [verifyRemarks, setVerifyRemarks] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [pastDate, setPastDate] = useState("");
  const [pastPasses, setPastPasses] = useState([]);
  const [pastLoading, setPastLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [readyRes, verifiedRes] = await Promise.all([
        getReadyForVerification(),
        getVerifiedToday()
      ]);
      setReady(readyRes.data);
      setVerified(verifiedRes.data);
    } catch (error) {
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      toast.error("Enter a search term");
      return;
    }
    setSearching(true);
    try {
      const { data } = await gateSearch(searchQuery);
      setSearchResults(data);
      if (data.length === 0) {
        toast.info("No matching approved passes found");
      }
    } catch (error) {
      toast.error("Search failed");
    } finally {
      setSearching(false);
    }
  };

  const handleVerify = async () => {
    setSubmitting(true);
    try {
      await verifyGatePass(selectedPass.id, {
        verified: true,
        remarks: verifyRemarks.trim() || null
      });
      toast.success("Gate pass verified! Opening print...");
      const verifiedPassId = selectedPass.id;
      setSelectedPass(null);
      setVerifyRemarks("");
      loadData();
      // Auto-open print in new tab after gate verification
      window.open(`/print/${verifiedPassId}`, "_blank");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Verification failed");
    } finally {
      setSubmitting(false);
    }
  };

  const loadPastDate = async () => {
    if (!pastDate) { toast.error("Select a date"); return; }
    setPastLoading(true);
    try {
      const { data } = await getGatePastDate(pastDate);
      setPastPasses(data);
    } catch { toast.error("Failed to load"); }
    finally { setPastLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="gate-operator-dashboard">
      <Tabs defaultValue="verify" className="space-y-4">
        <TabsList>
          <TabsTrigger value="verify" data-testid="go-verify-tab">Gate Verification</TabsTrigger>
          <TabsTrigger value="return" data-testid="go-return-tab">
            <ArrowDownLeft className="h-3 w-3 mr-1" /> Return Entry
          </TabsTrigger>
        </TabsList>

        <TabsContent value="verify">
      {/* Search Section */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Search className="h-5 w-5" />
            Search Gate Pass
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-3">
            <Input
              placeholder="Search by Request Number, Student Name, or Enrollment..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleSearch()}
              className="flex-1"
              data-testid="search-input"
            />
            <Button onClick={handleSearch} disabled={searching} data-testid="search-btn">
              {searching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
              <span className="ml-2">Search</span>
            </Button>
          </div>

          {/* Search Results */}
          {searchResults.length > 0 && (
            <div className="mt-4">
              <p className="text-sm text-slate-500 mb-2">Search Results ({searchResults.length})</p>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Request No.</TableHead>
                    <TableHead>Student</TableHead>
                    <TableHead>Exit Date</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {searchResults.map((req) => (
                    <TableRow key={req.id}>
                      <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                      <TableCell>{req.student_name}</TableCell>
                      <TableCell>{formatDate(req.exit_date)}</TableCell>
                      <TableCell>
                        <Button 
                          size="sm"
                          className="bg-emerald-600 hover:bg-emerald-700"
                          onClick={() => setSelectedPass(req)}
                          data-testid={`verify-btn-${req.id}`}
                        >
                          <ShieldCheck className="h-4 w-4 mr-1" />
                          Verify
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ready for Verification</p>
                <p className="text-2xl font-semibold text-amber-600">{ready.length}</p>
              </div>
              <QrCode className="h-8 w-8 text-amber-400" />
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Verified Today</p>
                <p className="text-2xl font-semibold text-emerald-600">{verified.length}</p>
              </div>
              <ShieldCheck className="h-8 w-8 text-emerald-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs defaultValue="ready" className="space-y-4">
        <TabsList>
          <TabsTrigger value="ready" data-testid="ready-tab">
            Ready ({ready.length})
          </TabsTrigger>
          <TabsTrigger value="verified" data-testid="verified-tab">
            Verified Today ({verified.length})
          </TabsTrigger>
          <TabsTrigger value="past" data-testid="past-tab">
            <Calendar className="h-4 w-4 mr-1" />
            Past Date
          </TabsTrigger>
        </TabsList>

        <TabsContent value="ready">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
                </div>
              ) : ready.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <CheckCircle2 className="h-12 w-12 mx-auto mb-4 text-slate-300" />
                  <p>No passes waiting for verification</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Course/Year</TableHead>
                      <TableHead>Exit Time</TableHead>
                      <TableHead>Destination</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {ready.map((req) => (
                      <TableRow key={req.id} data-testid={`ready-row-${req.id}`}>
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{req.student_name}</p>
                            <p className="text-xs text-slate-500">{req.student_enrollment}</p>
                          </div>
                        </TableCell>
                        <TableCell>{req.course_name} - Y{req.year}</TableCell>
                        <TableCell>{req.exit_time}</TableCell>
                        <TableCell className="max-w-[150px] truncate">{req.destination}</TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            <Button 
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700"
                              onClick={() => setSelectedPass(req)}
                              data-testid={`verify-btn-${req.id}`}
                            >
                              <ShieldCheck className="h-4 w-4 mr-1" />
                              Verify
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="verified">
          <Card className="border-slate-200">
            <CardContent className="p-0">
              {verified.length === 0 ? (
                <div className="text-center py-12 text-slate-500">
                  <p>No verifications today yet</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Exit Time</TableHead>
                      <TableHead>Verified At</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {verified.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>{req.student_name}</TableCell>
                        <TableCell>{req.exit_time}</TableCell>
                        <TableCell>{formatDateTime(req.gate_verification?.verification_time)}</TableCell>
                        <TableCell>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => navigate(`/print/${req.id}`)}
                          >
                            <Printer className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="past">
          <Card className="border-slate-200">
            <CardHeader className="pb-4">
              <CardTitle className="text-lg">Past Date Entries</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex items-end gap-3 mb-4">
                <div className="space-y-1">
                  <Label className="text-sm">Select Date</Label>
                  <Input
                    type="date"
                    value={pastDate}
                    max={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setPastDate(e.target.value)}
                    className="w-48"
                    data-testid="gate-past-date"
                  />
                </div>
                <Button onClick={loadPastDate} disabled={pastLoading} data-testid="gate-load-past">
                  {pastLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4 mr-1" />}
                  View
                </Button>
              </div>
              {pastPasses.length > 0 ? (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Request No.</TableHead>
                      <TableHead>Student</TableHead>
                      <TableHead>Exit Time</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Verified At</TableHead>
                      <TableHead>Reprint</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {pastPasses.map((req) => (
                      <TableRow key={req.id}>
                        <TableCell className="font-mono text-sm">{req.request_number}</TableCell>
                        <TableCell>{req.student_name}</TableCell>
                        <TableCell>{req.exit_time}</TableCell>
                        <TableCell><StatusBadge status={req.status} /></TableCell>
                        <TableCell>{req.gate_verification ? formatDateTime(req.gate_verification.verification_time) : "-"}</TableCell>
                        <TableCell>
                          <Button variant="ghost" size="sm" onClick={() => navigate(`/print/${req.id}`)} data-testid={`reprint-past-${req.id}`}>
                            <Printer className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <p className="text-center py-8 text-slate-400 text-sm">
                  {pastDate ? "No entries found for this date." : "Select a date to view past entries."}
                </p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Verification Dialog */}
      <Dialog open={!!selectedPass} onOpenChange={() => setSelectedPass(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
              Verify Gate Pass
            </DialogTitle>
          </DialogHeader>
          
          {selectedPass && (
            <div className="space-y-4">
              <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-lg space-y-3 text-sm">
                <div className="flex justify-between items-center">
                  <span className="text-slate-500">Request Number</span>
                  <span className="font-mono font-bold text-lg">{selectedPass.request_number}</span>
                </div>
                <div className="border-t border-emerald-200 pt-3 grid grid-cols-2 gap-2">
                  <div>
                    <p className="text-slate-500">Student</p>
                    <p className="font-medium">{selectedPass.student_name}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Enrollment</p>
                    <p className="font-medium">{selectedPass.student_enrollment}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Course/Year</p>
                    <p>{selectedPass.course_name} - Y{selectedPass.year}</p>
                  </div>
                  <div>
                    <p className="text-slate-500">Exit Time</p>
                    <p>{selectedPass.exit_date} at {selectedPass.exit_time}</p>
                  </div>
                  <div className="col-span-2">
                    <p className="text-slate-500">Destination</p>
                    <p>{selectedPass.destination}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Verification Remarks (Optional)</Label>
                <Textarea
                  placeholder="Add any remarks about the verification"
                  value={verifyRemarks}
                  onChange={(e) => setVerifyRemarks(e.target.value)}
                  data-testid="verify-remarks-input"
                />
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm">
                <p className="text-amber-800">
                  Please verify the student's identity with the printed gate pass before confirming.
                </p>
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedPass(null)}>Cancel</Button>
            <Button 
              onClick={handleVerify}
              disabled={submitting}
              className="bg-emerald-600 hover:bg-emerald-700"
              data-testid="confirm-verify-btn"
            >
              {submitting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <ShieldCheck className="h-4 w-4 mr-2" />
                  Confirm Exit
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Gate Pass Activity - at bottom */}
        </TabsContent>

        <TabsContent value="return">
          <GOReturnEntryTab />
        </TabsContent>
      </Tabs>

      <TodayPastPasses showPrint={true} />
    </div>
  );
};

/* Gate Operator Return Entry Tab */
const GOReturnEntryTab = () => {
  const navigate = useNavigate();
  const [activePasses, setActivePasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [returnDialog, setReturnDialog] = useState(null);
  const [returnData, setReturnData] = useState({ return_date: "", return_time: "", remarks: "" });
  const [submitting, setSubmitting] = useState(false);
  const todayStr = new Date().toISOString().split("T")[0];
  const nowTime = new Date().toTimeString().slice(0, 5);

  useEffect(() => { loadActive(); }, []);

  const loadActive = async () => {
    try {
      const { data } = await getWardenActivePasses();
      setActivePasses(data.filter(p => p.status === "gate_verified"));
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  };

  const handleReturn = async () => {
    if (!returnData.return_date || !returnData.return_time) {
      toast.error("Return date and time are required");
      return;
    }
    setSubmitting(true);
    try {
      await markReturnEntry(returnDialog.id, returnData);
      toast.success("Return entry recorded! Pass closed.");
      setReturnDialog(null);
      loadActive();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to record return");
    } finally { setSubmitting(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader2 className="h-8 w-8 animate-spin text-slate-400" /></div>;

  return (
    <Card className="border-slate-200" data-testid="go-return-entry-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <ArrowDownLeft className="h-5 w-5 text-emerald-600" />
          Student Return Entry
        </CardTitle>
      </CardHeader>
      <CardContent>
        {activePasses.length === 0 ? (
          <p className="text-center py-8 text-slate-400 text-sm">No students currently out (gate verified).</p>
        ) : (
          <div className="border rounded-lg overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 text-xs">
                  <TableHead className="text-xs">Req No.</TableHead>
                  <TableHead className="text-xs">Student</TableHead>
                  <TableHead className="text-xs">Course/Yr</TableHead>
                  <TableHead className="text-xs">Exit Date/Time</TableHead>
                  <TableHead className="text-xs">Expected Return</TableHead>
                  <TableHead className="text-xs">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {activePasses.map((p) => (
                  <TableRow key={p.id} data-testid={`go-active-${p.id}`}>
                    <TableCell className="font-mono text-xs">{p.request_number}</TableCell>
                    <TableCell className="font-medium text-sm">{p.student_name}</TableCell>
                    <TableCell className="text-xs">{p.course_name}-Y{p.year}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{formatDate(p.exit_date)} {p.exit_time}</TableCell>
                    <TableCell className="text-xs whitespace-nowrap">{p.expected_return_date ? `${formatDate(p.expected_return_date)} ${p.expected_return_time || ""}` : "-"}</TableCell>
                    <TableCell>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-700 text-xs h-7"
                        onClick={() => { setReturnDialog(p); setReturnData({ return_date: todayStr, return_time: nowTime, remarks: "" }); }}
                        data-testid={`go-mark-return-${p.id}`}
                      >
                        <ArrowDownLeft className="h-3 w-3 mr-1" /> Return
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>

      {/* Return Entry Dialog */}
      <Dialog open={!!returnDialog} onOpenChange={() => setReturnDialog(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Mark Student Return</DialogTitle>
          </DialogHeader>
          {returnDialog && (
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg text-sm">
                <p className="font-semibold">{returnDialog.student_name}</p>
                <p className="text-slate-500">{returnDialog.course_name} - Year {returnDialog.year}</p>
                <p className="text-xs text-slate-400 mt-1">Pass: {returnDialog.request_number}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-sm">Return Date *</Label>
                  <Input type="date" value={returnData.return_date} onChange={(e) => setReturnData({ ...returnData, return_date: e.target.value })} data-testid="go-return-date" />
                </div>
                <div className="space-y-1">
                  <Label className="text-sm">Return Time *</Label>
                  <Input type="time" value={returnData.return_time} onChange={(e) => setReturnData({ ...returnData, return_time: e.target.value })} data-testid="go-return-time" />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-sm">Remarks</Label>
                <Textarea value={returnData.remarks} onChange={(e) => setReturnData({ ...returnData, remarks: e.target.value })} placeholder="Any remarks" rows={2} data-testid="go-return-remarks" />
              </div>
              <div className="flex gap-2">
                <Button onClick={handleReturn} disabled={submitting} data-testid="go-confirm-return" className="bg-emerald-600 hover:bg-emerald-700">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <ArrowDownLeft className="h-4 w-4 mr-1" />}
                  Confirm Return & Close Pass
                </Button>
                <Button variant="outline" onClick={() => setReturnDialog(null)}>Cancel</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default GateOperatorDashboard;
